import React, { useEffect, useMemo, useState } from "react";
import { db, auth } from "../../firebase";
import { formatCurrencyUY } from "../../utils/formatters";
import { detalleCorto } from "../../utils/detalleGasto";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  serverTimestamp,
  doc,
  getDoc,
} from "firebase/firestore";
import Loader from "../../components/Loader";
import { tolerantId } from "../../utils/firestoreHelpers";
import ScreenHeader from "../../components/ScreenHeader";
import { FaPlus, FaCreditCard } from "react-icons/fa";
import { useBuilding } from "../../context/BuildingContext";
import {
  colors,
  shadows,
  radius,
  spacing,
  typography,
  transitions,
  cardBase,
  btnPrimary,
  btnSecondary,
  btnDanger,
  btnGhost,
  inputBase,
  pageContainer,
  pageTitle,
  tableHeader,
  tableCell,
  modalOverlay as modalOverlayToken,
} from "../../utils/designTokens";

const PagosUnidad = ({ unidadId, setSelected }) => {
  const { selectedBuilding } = useBuilding();
  const [pagos, setPagos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [unidad, setUnidad] = useState(null);
  const [desglosePayments, setDesglosePayments] = useState({}); // { paymentId: { ownerAmount, tenantAmount } }
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detallePago, setDetallePago] = useState(null);
  const [gastosDetalle, setGastosDetalle] = useState([]);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [desgloseGastos, setDesgloseGastos] = useState({}); // { expenseId: { ownerAmount, tenantAmount } }

  const [notification, setNotification] = useState(null);

  const [form, setForm] = useState({
    amount: "",
    date: "",
    description: "",
    method: "transfer", // transfer | card | cash
    paid: true,
  });

  const fetchPagos = async () => {
    try {
      setLoading(true);

      let pagosCierreMes = [];
      let unidadData = null;
      if (unidadId) {
        const unidadDoc = await getDoc(doc(db, "apartments", unidadId));
        if (unidadDoc.exists()) {
          unidadData = unidadDoc.data();
          setUnidad(unidadData);
        }
      }

      // Pagos manuales de la unidad (unitId), acotados al edificio de la unidad:
      // con las reglas 0D una consulta de payments solo por unitId no prueba la
      // regla, y así tampoco entra un pago de otro edificio con este unitId.
      const unidadBid = unidadData?.building ?? selectedBuilding?.buildingid;
      const porId = new Map();
      if (unidadId && unidadBid != null && unidadBid !== "") {
        const snapUnitId = await getDocs(query(
          collection(db, "payments"),
          where("unitId", "==", unidadId),
          where("buildingid", "in", tolerantId(unidadBid))
        ));
        snapUnitId.docs.forEach((d) => porId.set(d.id, { id: d.id, ...d.data() }));
      }
      // Pagos manuales viejos SIN buildingid (antes PagosUnidad no lo grababa;
      // pendiente su backfill). Best-effort: si la regla no deja la consulta
      // solo por unitId, quedan fuera hasta el backfill.
      if (unidadId) {
        try {
          const snapLegado = await getDocs(query(collection(db, "payments"), where("unitId", "==", unidadId)));
          snapLegado.docs.forEach((d) => {
            const data = d.data();
            if (data.buildingid == null && data.buildingId == null) porId.set(d.id, { id: d.id, ...data });
          });
        } catch (legadoErr) {
          console.warn("Pagos manuales viejos (sin edificio) no disponibles:", legadoErr?.code || legadoErr?.message);
        }
      }
      const pagosUnitId = [...porId.values()];

      // Buscar pagos de cierre de mes relacionados con esta unidad
      // Los pagos de cierre de mes tienen userid (del owner o tenant) y buildingid

      if (unidadData && selectedBuilding?.buildingid) {
        const ownerId = unidadData.owner ? String(unidadData.owner) : null;
        const tenantId = unidadData.tenant ? String(unidadData.tenant) : null;

        // Sin lecturas de `users`: esta pantalla no muestra los nombres del
        // propietario ni del inquilino (las filas dicen el rol), y con las
        // reglas 0D (C10) una consulta de fichas ajenas que no fija accessBids
        // se niega entera y cortaba la carga de los pagos.

        // Buscar todos los pagos de cierre de mes del edificio (para encontrar todos los meses/años)
        // Luego filtraremos por los que corresponden a esta unidad
        const qAllCierres = query(
          collection(db, "payments"),
          where("buildingid", "==", Number(selectedBuilding.buildingid))
        );
        const snapAllCierres = await getDocs(qAllCierres);
        const allCierres = snapAllCierres.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter(p => p.month && p.year);
        // Si el payment tiene apartmentId, matchea solo con esta unidad.
        // Si NO tiene (multas/amenity/legacy del usuario), fallback heurístico
        // por userid contra owner/tenant — así las multas y cargos del usuario
        // se muestran bajo la unidad donde es residente.
        const todosLosCierres = allCierres.filter(p => {
          if (p.apartmentId) return p.apartmentId === unidadId;
          if (p.unitId) return p.unitId === unidadId;
          return p.userid === ownerId || p.userid === tenantId;
        });
        pagosCierreMes.push(...todosLosCierres);
      }
      
      // Crear lista unificada de pagos con información de propietario/inquilino
      const pagosUnificados = [];
      
      if (unidadData) {
        const ownerId = unidadData.owner ? String(unidadData.owner) : null;
        const tenantId = unidadData.tenant ? String(unidadData.tenant) : null;
        
        // Agregar pagos manuales (se muestran una vez)
        pagosUnitId.forEach(p => {
          pagosUnificados.push({ ...p, tipo: "manual", rol: null });
        });
        
        // Agrupar pagos de cierre de mes por month/year para crear filas separadas
        const cierresAgrupados = {};
        pagosCierreMes.forEach(p => {
          const key = `${p.month}-${p.year}`;
          if (!cierresAgrupados[key]) {
            cierresAgrupados[key] = [];
          }
          cierresAgrupados[key].push(p);
        });
        
        // Para cada cierre de mes, crear filas para owner y tenant
        Object.values(cierresAgrupados).forEach(pagosDelCierre => {
          if (pagosDelCierre.length === 0) return;
          
          // Usar el primer pago como base (todos tienen el mismo month/year)
          const pagoBase = pagosDelCierre[0];
          
          // Crear fila para el propietario si existe
          if (ownerId) {
            // Buscar si hay un payment específico del owner
            const pagoOwner = pagosDelCierre.find(p => p.userid === ownerId);
            if (pagoOwner) {
              pagosUnificados.push({ 
                ...pagoOwner, 
                tipo: "cierre", 
                rol: "Propietario",
                userId: ownerId,
                uniqueId: `${pagoOwner.id}-owner` // ID único para React
              });
            } else {
              // Si no hay payment del owner, crear uno basado en el base pero con ID único
              pagosUnificados.push({ 
                ...pagoBase, 
                id: `${pagoBase.id}-owner`, // ID único
                tipo: "cierre", 
                rol: "Propietario",
                userId: ownerId,
                userid: ownerId, // Asegurar que tenga el userid correcto
                uniqueId: `${pagoBase.id}-owner`
              });
            }
          }
          
          // Crear fila para el inquilino si existe (y es diferente del owner)
          if (tenantId && tenantId !== ownerId) {
            // Buscar si hay un payment específico del tenant
            const pagoTenant = pagosDelCierre.find(p => p.userid === tenantId);
            if (pagoTenant) {
              pagosUnificados.push({ 
                ...pagoTenant, 
                tipo: "cierre", 
                rol: "Inquilino",
                userId: tenantId,
                uniqueId: `${pagoTenant.id}-tenant` // ID único para React
              });
            } else {
              // Si no hay payment del tenant, crear uno basado en el base pero con ID único
              pagosUnificados.push({ 
                ...pagoBase, 
                id: `${pagoBase.id}-tenant`, // ID único
                tipo: "cierre", 
                rol: "Inquilino",
                userId: tenantId,
                userid: tenantId, // Asegurar que tenga el userid correcto
                uniqueId: `${pagoBase.id}-tenant`
              });
            }
          }
        });
      } else {
        // Si no hay unidad, solo mostrar pagos manuales
        pagosUnitId.forEach(p => {
          pagosUnificados.push({ ...p, tipo: "manual", rol: null });
        });
      }
      
      // Ordenar por fecha desc
      pagosUnificados.sort((a, b) => {
        const dateA = a.date || a.maturity || "";
        const dateB = b.date || b.maturity || "";
        return dateB.localeCompare(dateA);
      });
      
      setPagos(pagosUnificados);
      
      // Cargar desglose para pagos de cierre de mes
      // Usar los IDs originales de los payments (sin sufijos -owner/-tenant)
      if (unidadData && selectedBuilding?.buildingid) {
        const allCierreMes = pagosUnificados.filter(p => p.month && p.year);
        // Obtener IDs únicos originales (sin sufijos)
        const idsOriginales = new Set();
        allCierreMes.forEach(p => {
          const idOriginal = p.id.includes('-') ? p.id.split('-')[0] : p.id;
          idsOriginales.add(idOriginal);
        });
        // Crear array con payments únicos usando IDs originales
        const pagosUnicos = Array.from(idsOriginales).map(idOriginal => {
          return allCierreMes.find(p => {
            const pId = p.id.includes('-') ? p.id.split('-')[0] : p.id;
            return pId === idOriginal;
          });
        }).filter(p => p);
        await cargarDesglosePayments(pagosUnicos, unidadId);
      }
    } finally {
      setLoading(false);
    }
  };
  
  const cargarDesglosePayments = async (pagosCierreMes, apartmentId) => {
    try {
      if (!selectedBuilding?.buildingid || pagosCierreMes.length === 0) {
        setDesglosePayments({});
        return;
      }
      
      const desglose = {};
      
      // Para cada pago de cierre de mes, buscar el desglose específico de esta unidad
      for (const pago of pagosCierreMes) {
        // Usar el ID original del payment (sin sufijos -owner/-tenant)
        const paymentIdOriginal = pago.id.includes('-') ? pago.id.split('-')[0] : pago.id;
        
        const expensesApartmentsQuery = query(
          collection(db, "expensesapartments"),
          where("buildingid", "==", Number(selectedBuilding.buildingid)),
          where("apartment", "==", apartmentId),
          where("month", "==", pago.month),
          where("year", "==", pago.year)
        );
        const expensesApartmentsSnap = await getDocs(expensesApartmentsQuery);
        const expensesApartments = expensesApartmentsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        // Sumar los montos owner y tenant de esta unidad específica
        let ownerAmount = 0;
        let tenantAmount = 0;
        
        for (const ea of expensesApartments) {
          ownerAmount += Number(ea.owner || 0);
          tenantAmount += Number(ea.tenant || 0);
        }
        
        // Guardar el desglose usando el ID original del payment
        desglose[paymentIdOriginal] = {
          ownerAmount: Math.round(ownerAmount * 100) / 100,
          tenantAmount: Math.round(tenantAmount * 100) / 100,
        };
      }
      
      setDesglosePayments(desglose);
    } catch (error) {
      console.error("Error cargando desglose de payments:", error);
      setDesglosePayments({});
    }
  };

  useEffect(() => {
    if (!unidadId) return;
    fetchPagos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unidadId, selectedBuilding?.buildingid]);

  const totals = useMemo(() => {
    const allPagos = Array.isArray(pagos) ? pagos : [];
    const paid = allPagos.filter((p) => p.paid).reduce((acc, p) => acc + Number(p.amount || 0), 0);
    const pending = allPagos.filter((p) => !p.paid).reduce((acc, p) => acc + Number(p.amount || 0), 0);
    return { paid, pending, total: paid + pending };
  }, [pagos]);
  
  const verDetalle = async (pago) => {
    setDetallePago(pago);
    setShowDetailModal(true);
    setLoadingDetalle(true);
    setGastosDetalle([]);
    setDesgloseGastos({});
    
    try {
      // Si el pago tiene expenseIds, cargar esos gastos
      if (pago.expenseIds && pago.expenseIds.length > 0) {
        const gastosPromises = pago.expenseIds.map(async (expenseId) => {
          try {
            const expenseDoc = await getDoc(doc(db, "expenses", expenseId));
            if (expenseDoc.exists()) {
              return { id: expenseDoc.id, ...expenseDoc.data() };
            }
            return null;
          } catch (error) {
            console.error(`Error cargando expense ${expenseId}:`, error);
            return null;
          }
        });
        
        const gastos = await Promise.all(gastosPromises);
        const gastosFiltrados = gastos.filter(g => g !== null);
        setGastosDetalle(gastosFiltrados);
        
        // Cargar desglose de gastos para esta unidad
        if (unidadId && selectedBuilding?.buildingid && pago.month && pago.year) {
          const expensesApartmentsQuery = query(
            collection(db, "expensesapartments"),
            where("buildingid", "==", Number(selectedBuilding.buildingid)),
            where("apartment", "==", unidadId),
            where("month", "==", pago.month),
            where("year", "==", pago.year)
          );
          const expensesApartmentsSnap = await getDocs(expensesApartmentsQuery);
          const expensesApartments = expensesApartmentsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          
          const desglose = {};
          gastosFiltrados.forEach(gasto => {
            const ea = expensesApartments.find(e => e.expenseId === gasto.id);
            if (ea) {
              // Mostrar solo el monto correspondiente al rol del pago
              if (pago.rol === "Propietario") {
                desglose[gasto.id] = {
                  amount: Math.round(Number(ea.owner || 0) * 100) / 100,
                };
              } else if (pago.rol === "Inquilino") {
                desglose[gasto.id] = {
                  amount: Math.round(Number(ea.tenant || 0) * 100) / 100,
                };
              } else {
                desglose[gasto.id] = {
                  ownerAmount: Math.round(Number(ea.owner || 0) * 100) / 100,
                  tenantAmount: Math.round(Number(ea.tenant || 0) * 100) / 100,
                };
              }
            }
          });
          setDesgloseGastos(desglose);
        }
      } else {
        setGastosDetalle([]);
      }
    } catch (error) {
      console.error("Error cargando detalle:", error);
      setGastosDetalle([]);
    } finally {
      setLoadingDetalle(false);
    }
  };

  const savePago = async () => {
    // Validación básica
    const amountNum = Number(form.amount);
    if (!amountNum || !form.date) {
      setNotification({ type: "error", message: "Completa monto y fecha." });
      setTimeout(() => setNotification(null), 4000);
      return;
    }
    // FIN-052: Validar monto positivo y finito
    if (amountNum <= 0 || !isFinite(amountNum)) {
      setNotification({ type: "error", message: "El monto debe ser un número positivo." });
      setTimeout(() => setNotification(null), 4000);
      return;
    }

    // El pago lleva el edificio de la unidad: las reglas 0D exigen que el staff
    // sea del edificio del pago, y sin buildingid no lo ve ninguna consulta
    // por edificio (cobranza, estado de cuenta).
    const pagoBid = Number(unidad?.building ?? selectedBuilding?.buildingid);
    if (!Number.isFinite(pagoBid) || pagoBid <= 0) {
      setNotification({ type: "error", message: "Seleccioná el edificio de la unidad antes de registrar el pago." });
      setTimeout(() => setNotification(null), 4000);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        unitId: unidadId,
        buildingid: pagoBid,
        amount: amountNum,
        date: form.date, // ISO (YYYY-MM-DD) o como venís usando
        description: form.description || "",
        method: form.method,
        paid: !!form.paid,
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.email || "desconocido",
      };

      const ref = await addDoc(collection(db, "payments"), payload);

      // Audit
      await addDoc(collection(db, "audit"), {
        action: "create_payment",
        unitId: unidadId,
        paymentId: ref.id,
        user: auth.currentUser?.email || "desconocido",
        timestamp: serverTimestamp(),
        changes: { after: payload, fields: Object.keys(payload) },
      });

      setShowNew(false);
      setForm({ amount: "", date: "", description: "", method: "transfer", paid: true });
      fetchPagos();
    } catch (e) {
      console.error(e);
      setNotification({ type: "error", message: "Error guardando el pago." });
      setTimeout(() => setNotification(null), 4000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div style={styles.page}>
      <ScreenHeader
        icon={FaCreditCard}
        title="Pagos de la Unidad"
        subtitle={unidad ? `Unidad ${unidad.number}` : ""}
        onBack={() => setSelected("unidad")}
      />

      {/* Notificación inline */}
      {notification && (
        <div style={{
          padding: `${spacing[3]}px ${spacing[4]}px`,
          marginBottom: spacing[4],
          borderRadius: radius.sm,
          backgroundColor: notification.type === "error" ? colors.errorLight : colors.successLight,
          color: notification.type === "error" ? colors.errorDark : colors.success,
          border: `1px solid ${notification.type === "error" ? colors.error : colors.success}`,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: typography.sizes.md,
        }}>
          <span>{notification.message}</span>
          <button onClick={() => setNotification(null)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: typography.sizes.xl, color: "inherit" }} aria-label="Cerrar notificación">×</button>
        </div>
      )}

      {/* KPIs */}
      <div style={styles.kpis}>
        <div style={{ ...styles.kpi, ...styles.kpiBlue }}>
          <span style={styles.kpiLabel}>Total</span>
          <span style={styles.kpiValue}>{formatCurrencyUY(totals.total)}</span>
        </div>
        <div style={{ ...styles.kpi, ...styles.kpiGreen }}>
          <span style={styles.kpiLabel}>Pagado</span>
          <span style={styles.kpiValue}>{formatCurrencyUY(totals.paid)}</span>
        </div>
        <div style={{ ...styles.kpi, ...styles.kpiOrange }}>
          <span style={styles.kpiLabel}>Pendiente</span>
          <span style={styles.kpiValue}>{formatCurrencyUY(totals.pending)}</span>
        </div>
      </div>

      {/* Lista de pagos unificada */}
      {(!pagos || pagos.length === 0) ? (
        <div style={styles.empty}>
          <FaCreditCard size={22} />
          <p style={{ margin: 8 }}>No hay pagos registrados para esta unidad.</p>
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
        <div style={styles.table}>
          <div style={styles.thead}>
            <div>Persona</div>
            <div>Monto</div>
            <div>Fecha</div>
            <div>Método</div>
            <div>Estado</div>
            <div>Desglose</div>
            <div>Acciones</div>
          </div>
          {pagos.map((p, index) => {
            const desglose = desglosePayments[p.id];
            const esCierreMes = p.month && p.year;
            
            // Mostrar solo el rol
            let personaLabel = "—";
            if (esCierreMes && p.rol) {
              personaLabel = p.rol;
            }
            
            // Calcular monto según el rol
            let montoMostrar = Number(p.amount || 0);
            if (esCierreMes && p.rol) {
              // Buscar el desglose usando el ID original del payment (sin el sufijo -owner/-tenant)
              const originalPaymentId = p.id.includes('-') ? p.id.split('-')[0] : p.id;
              const desgloseOriginal = desglosePayments[originalPaymentId];
              
              if (desgloseOriginal) {
                // Para pagos de cierre de mes, usar el monto específico del rol
                if (p.rol === "Propietario") {
                  montoMostrar = desgloseOriginal.ownerAmount || 0;
                } else if (p.rol === "Inquilino") {
                  montoMostrar = desgloseOriginal.tenantAmount || 0;
                }
              }
            }
            
            return (
              <div key={p.uniqueId || `${p.id}-${p.rol || index}`} style={styles.trow}>
                <div>{personaLabel}</div>
                <div>{formatCurrencyUY(montoMostrar)}</div>
                <div>{p.date || p.maturity || "—"}</div>
                <div style={styles.badge}>{p.method ? mapMethod(p.method) : "—"}</div>
                <div style={p.paid ? styles.badgeSuccess : styles.badgeWarn}>
                  {p.paid ? "Pagado" : "Pendiente"}
                </div>
                <div>{p.description || "—"}</div>
                <div>
                  {esCierreMes && (
                    <button
                      style={styles.detailButton}
                      onClick={() => verDetalle(p)}
                    >
                      Ver Detalle
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        </div>
      )}

      {/* Botón + crear pago */}
      <button
        style={styles.fab}
        title="Crear pago"
        onClick={() => setShowNew(true)}
      >
        <FaPlus size={22} />
      </button>

      {/* Modal nuevo pago */}
      {showNew && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Nuevo Pago</h2>
              <button
                style={styles.iconBtn}
                onClick={() => setShowNew(false)}
                title="Cerrar"
              >
                ✕
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Monto</label>
                <input
                  type="number"
                  style={styles.input}
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  min={0}
                  placeholder="Ej: 3500"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Fecha</label>
                <input
                  type="date"
                  style={styles.input}
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Método</label>
                <select
                  style={styles.input}
                  value={form.method}
                  onChange={(e) => setForm({ ...form, method: e.target.value })}
                >
                  <option value="transfer">Transferencia</option>
                  <option value="card">Tarjeta</option>
                  <option value="cash">Efectivo</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Estado</label>
                <select
                  style={styles.input}
                  value={form.paid ? "true" : "false"}
                  onChange={(e) => setForm({ ...form, paid: e.target.value === "true" })}
                >
                  <option value="true">Pagado</option>
                  <option value="false">Pendiente</option>
                </select>
              </div>
              <div style={{ gridColumn: "1 / -1", ...styles.formGroup }}>
                <label style={styles.label}>Detalle</label>
                <input
                  style={styles.input}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Ej: Gastos comunes julio"
                />
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button
                style={styles.btnSecondary}
                onClick={() => setShowNew(false)}
                disabled={saving}
              >
                Cancelar
              </button>
              <button
                style={{ ...styles.btnPrimary, opacity: saving ? 0.7 : 1 }}
                onClick={savePago}
                disabled={saving}
              >
                {saving ? "Guardando..." : "Guardar pago"}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal Ver Detalle */}
      {showDetailModal && detallePago && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={{...styles.modalHeader, flexDirection: "row", justifyContent: "space-between", alignItems: "center"}}>
              <h3 style={styles.modalTitle}>Detalle del Cierre de Mes</h3>
              <button
                style={styles.closeButton}
                onClick={() => {
                  setShowDetailModal(false);
                  setDetallePago(null);
                  setGastosDetalle([]);
                  setDesgloseGastos({});
                }}
              >
                ✕
              </button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.detailInfo}>
                <p style={styles.detailInfoParagraph}><strong>Descripción:</strong> {detallePago.description}</p>
                <p style={styles.detailInfoParagraph}><strong>Vencimiento:</strong> {detallePago.maturity}</p>
                <p style={styles.detailInfoParagraph}><strong>Importe Total:</strong> {formatCurrencyUY(detallePago.amount)}</p>
              </div>
              
              <h4 style={styles.gastosTitle}>Gastos incluidos:</h4>
              
              {loadingDetalle ? (
                <p style={styles.loadingText}>Cargando gastos...</p>
              ) : gastosDetalle.length === 0 ? (
                <p style={styles.emptyText}>No se encontraron gastos para este cierre.</p>
              ) : (
                <div style={styles.gastosList}>
                  <table style={styles.detailTable}>
                    <thead>
                      <tr>
                        <th style={styles.detailTh}>Descripción</th>
                        <th style={styles.detailTh}>Fecha Factura</th>
                        <th style={styles.detailTh}>Monto Total</th>
                        <th style={styles.detailTh}>Tu Parte</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gastosDetalle
                        .map((gasto) => {
                          let invoiceDateStr = "N/A";
                          try {
                            if (typeof gasto.invoiceDate === 'string') {
                              invoiceDateStr = gasto.invoiceDate;
                            } else if (gasto.invoiceDate?.toDate) {
                              invoiceDateStr = gasto.invoiceDate.toDate().toLocaleDateString('es-UY');
                            } else if (gasto.invoiceDate?.seconds) {
                              invoiceDateStr = new Date(gasto.invoiceDate.seconds * 1000).toLocaleDateString('es-UY');
                            }
                          } catch (e) {
                            invoiceDateStr = "N/A";
                          }
                          
                          const desgloseGasto = desgloseGastos[gasto.id];
                          const montoUsuario = desgloseGasto 
                            ? (desgloseGasto.amount !== undefined ? desgloseGasto.amount : 
                               (detallePago.rol === "Propietario" ? desgloseGasto.ownerAmount : desgloseGasto.tenantAmount))
                            : 0;
                          
                          return { gasto, invoiceDateStr, montoUsuario };
                        })
                        .filter(({ montoUsuario }) => montoUsuario > 0)
                        .map(({ gasto, invoiceDateStr, montoUsuario }) => (
                          <tr key={gasto.id}>
                            <td style={styles.detailTd} title={gasto.description || ""}>{detalleCorto(gasto) || "Sin descripción"}</td>
                            <td style={styles.detailTd}>{invoiceDateStr}</td>
                            <td style={styles.detailTd}>{formatCurrencyUY(gasto.amount)}</td>
                            <td style={styles.detailTd}>
                              {formatCurrencyUY(montoUsuario)}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div style={styles.modalFooter}>
              <button
                style={styles.closeModalButton}
                onClick={() => {
                  setShowDetailModal(false);
                  setDetallePago(null);
                  setGastosDetalle([]);
                  setDesgloseGastos({});
                }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const mapMethod = (m) =>
  m === "card" ? "Tarjeta" : m === "cash" ? "Efectivo" : "Transferencia";

// ─── Estilos con design tokens ──────────────────────────────────────────────
const styles = {
  // Contenedor principal de la página
  page: {
    ...pageContainer,
    position: "relative",
  },
  // Cabecera (usado por ScreenHeader, se conserva por compatibilidad)
  header: {
    display: "flex",
    alignItems: "center",
    gap: spacing[4],
    marginBottom: spacing[5],
  },
  // Botón de volver
  backBtn: {
    ...btnSecondary,
    gap: spacing[1],
    boxShadow: shadows.xs,
    transition: `all ${transitions.normal}`,
  },
  // Título de pantalla
  title: {
    ...pageTitle,
    fontSize: typography.sizes.xl,
    display: "flex",
    alignItems: "center",
  },

  // ─── KPIs ─────────────────────────────────────────────────────────────
  kpis: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: spacing[3],
    marginBottom: spacing[4],
  },
  kpi: {
    borderRadius: radius.md,
    padding: spacing[3],
    color: colors.white,
    boxShadow: shadows.md,
    display: "flex",
    flexDirection: "column",
    gap: spacing[1],
  },
  kpiLabel: { fontSize: typography.sizes.sm, opacity: 0.9 },
  kpiValue: { fontSize: 20, fontWeight: typography.weights.normal },
  kpiBlue: { background: colors.primary },
  kpiGreen: { background: colors.success },
  kpiOrange: { background: colors.warning },

  // ─── Tabla de pagos ───────────────────────────────────────────────────
  table: {
    ...cardBase,
    padding: 0,
    overflow: "hidden",
  },
  section: {
    marginBottom: spacing[6],
  },
  sectionTitle: {
    fontSize: typography.sizes.xl,
    fontWeight: typography.weights.normal,
    marginBottom: spacing[3],
    color: colors.gray900,
  },
  thead: {
    display: "grid",
    gridTemplateColumns: "180px 140px 140px 140px 120px 1fr 120px",
    gap: 0,
    padding: `${spacing[3]}px ${spacing[4]}px`,
    background: colors.gray50,
    color: colors.gray600,
    fontWeight: typography.weights.normal,
    fontSize: typography.sizes.base,
  },
  trow: {
    display: "grid",
    gridTemplateColumns: "180px 140px 140px 140px 120px 1fr 120px",
    padding: `${spacing[3]}px ${spacing[4]}px`,
    borderTop: `1px solid ${colors.gray200}`,
    fontSize: typography.sizes.md,
    alignItems: "center",
    background: colors.white,
  },
  // Botón para ver detalle de un pago
  detailButton: {
    ...btnPrimary,
    padding: `${spacing[1]}px ${spacing[3]}px`,
    fontSize: typography.sizes.sm,
  },
  // Badge genérico (método de pago)
  badge: {
    display: "inline-block",
    padding: `${spacing[1]}px ${spacing[2]}px`,
    borderRadius: radius.full,
    background: colors.gray200,
    fontSize: typography.sizes.sm,
    color: colors.gray900,
    textAlign: "center",
  },
  // Badge estado pagado
  badgeSuccess: {
    display: "inline-block",
    padding: `${spacing[1]}px ${spacing[2]}px`,
    borderRadius: radius.full,
    background: colors.successLight,
    color: colors.successDark,
    fontSize: typography.sizes.sm,
    textAlign: "center",
  },
  // Badge estado pendiente
  badgeWarn: {
    display: "inline-block",
    padding: `${spacing[1]}px ${spacing[2]}px`,
    borderRadius: radius.full,
    background: colors.errorLight,
    color: colors.errorDark,
    fontSize: typography.sizes.sm,
    textAlign: "center",
  },

  // ─── FAB (botón flotante para crear pago) ─────────────────────────────
  fab: {
    position: "fixed",
    right: spacing[6],
    bottom: spacing[6],
    width: 52,
    height: 52,
    display: "grid",
    placeItems: "center",
    borderRadius: "50%",
    background: colors.primary,
    color: colors.white,
    border: "none",
    boxShadow: shadows.lg,
    cursor: "pointer",
    transition: `all ${transitions.fast}`,
  },

  // ─── Modal (overlay + contenedor) ─────────────────────────────────────
  modalOverlay: {
    ...modalOverlayToken,
  },
  modal: {
    width: "min(640px, 92vw)",
    maxWidth: "92vw",
    maxHeight: "85vh",
    background: colors.white,
    borderRadius: radius.lg,
    boxShadow: shadows.modal,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  modalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: `${spacing[3]}px ${spacing[4]}px`,
    borderBottom: `1px solid ${colors.gray200}`,
  },
  modalTitle: {
    margin: 0,
    fontSize: typography.sizes.xl,
    fontWeight: typography.weights.normal,
    color: colors.gray900,
  },
  // Botón de cerrar modal (icono)
  iconBtn: {
    ...btnGhost,
    padding: spacing[1],
  },
  modalBody: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: spacing[3],
    padding: spacing[4],
    overflowY: "auto",
    flex: 1,
    minHeight: 0,
  },
  formGroup: { display: "flex", flexDirection: "column", gap: spacing[1] },
  // Label de formulario
  label: {
    fontSize: typography.sizes.sm,
    color: colors.gray600,
    fontWeight: typography.weights.normal,
  },
  // Input de formulario
  input: {
    ...inputBase,
    height: 36,
    padding: `${spacing[1]}px ${spacing[2]}px`,
  },
  modalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: spacing[2],
    padding: spacing[4],
    borderTop: `1px solid ${colors.gray200}`,
    background: colors.gray50,
  },
  // Botón secundario (cancelar)
  btnSecondary: {
    ...btnSecondary,
  },
  // Botón primario (guardar)
  btnPrimary: {
    ...btnPrimary,
  },

  // ─── Estado vacío ─────────────────────────────────────────────────────
  empty: {
    ...cardBase,
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
    color: colors.gray600,
  },

  // ─── Desglose propietario / inquilino ─────────────────────────────────
  desgloseContainer: {
    display: "flex",
    flexDirection: "column",
    gap: spacing[1],
  },
  desgloseTotal: {
    fontWeight: typography.weights.normal,
    fontSize: typography.sizes.md,
    color: colors.gray900,
  },
  desgloseDetalle: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    marginTop: spacing[1],
    fontSize: typography.sizes.sm,
    color: colors.gray600,
  },
  desgloseItem: {
    display: "flex",
    justifyContent: "space-between",
    gap: spacing[2],
  },
  desgloseLabel: {
    color: colors.gray600,
  },
  desgloseAmount: {
    fontWeight: typography.weights.normal,
    color: colors.gray900,
  },

  // ─── Modal de detalle de cierre de mes ────────────────────────────────
  closeButton: {
    ...btnGhost,
    fontSize: 20,
    padding: spacing[1],
  },
  detailInfo: {
    marginBottom: spacing[5],
    padding: spacing[3],
    backgroundColor: colors.gray50,
    borderRadius: radius.sm,
  },
  detailInfoParagraph: {
    margin: `${spacing[2]}px 0`,
    fontSize: typography.sizes.md,
    color: colors.gray900,
  },
  gastosTitle: {
    fontSize: typography.sizes.lg,
    fontWeight: typography.weights.normal,
    margin: `${spacing[5]}px 0 ${spacing[3]}px 0`,
    color: colors.gray900,
  },
  loadingText: {
    textAlign: "center",
    color: colors.gray600,
    fontStyle: "italic",
    padding: spacing[5],
  },
  emptyText: {
    textAlign: "center",
    color: colors.gray600,
    fontStyle: "italic",
    padding: spacing[5],
  },
  gastosList: {
    marginTop: spacing[3],
  },
  // Tabla de detalle de gastos dentro del modal
  detailTable: {
    width: "100%",
    borderCollapse: "collapse",
    backgroundColor: colors.white,
    borderRadius: radius.sm,
    overflow: "hidden",
  },
  detailTh: {
    textAlign: "left",
    padding: `${spacing[2]}px ${spacing[3]}px`,
    fontSize: typography.sizes.base,
    fontWeight: typography.weights.normal,
    color: colors.gray900,
    backgroundColor: colors.gray100,
    borderBottom: `2px solid ${colors.gray200}`,
  },
  detailTd: {
    padding: `${spacing[2]}px ${spacing[3]}px`,
    fontSize: typography.sizes.base,
    color: colors.gray900,
    borderBottom: `1px solid ${colors.gray100}`,
  },
  // Botón cerrar en el footer del modal de detalle
  closeModalButton: {
    ...btnSecondary,
    background: colors.gray400,
    color: colors.white,
    border: "none",
  },
};

export default PagosUnidad;
